import { UserFacingError } from '@features/shared/api/client'
import { useAuth, useUser } from '@clerk/clerk-react'
import { useMicrosoftSenderDirectory } from '@features/integrations/hooks/useMicrosoftSenderDirectory'
import type { MicrosoftSenderChoice } from '../logic/microsoftSenderOptions'

export function useMicrosoftSenders() {
  const directory = useMicrosoftSenderDirectory()
  const { userId } = useAuth()
  const { user } = useUser()
  const { query, connectionQuery, enabled } = directory
  const primaryEmail = user?.primaryEmailAddress
  const identityEmail = primaryEmail?.emailAddress.trim().toLowerCase()
  const senders: MicrosoftSenderChoice[] =
    directory.canUseIdentity &&
    user?.id === userId &&
    primaryEmail?.verification.status === 'verified' &&
    identityEmail
      ? [
          {
            source: 'identity',
            sender_email: identityEmail,
            display_name: user?.fullName?.trim() || identityEmail,
            is_own_mailbox: true,
          },
        ]
      : directory.senders
  /**
   * The chosen sender's connection, for the step to keep. Picking from the
   * list is the permission check: it holds only the mailboxes the person may
   * send from, and publishing or testing checks the sender again on the
   * server, as the person doing it (`backend/app/automations/senders.py`).
   * Apply waits only for the connection's status, when Myself is picked
   * before it has loaded.
   */
  const validate = async (email: string, signal?: AbortSignal) => {
    const selected = senders.find((sender) => sender.sender_email === email)
    if (!selected || (!('source' in selected) && !enabled)) {
      throw new UserFacingError('Choose a verified sender from the list.')
    }
    const connection =
      connectionQuery.data ??
      (await connectionQuery.refetch({ cancelRefetch: false, throwOnError: true })).data
    signal?.throwIfAborted()
    if (!directory.isCurrent(connection) || !connection?.connection_id) {
      throw new UserFacingError('Connect Microsoft 365 to use this sender.')
    }
    if (!('source' in selected) && query.data?.connection_id !== connection.connection_id) {
      void query.refetch()
      throw new UserFacingError('The Microsoft connection changed. Choose the sender again.')
    }
    return { connection_id: connection.connection_id }
  }
  return { ...directory, senders, hasChoices: directory.hasData || senders.length > 0, validate }
}
